<template>
    <div v-if="hasAccess">
        <top-nav-bar goto="/timesheets">
            <router-link :to="{ name: 'timesheets-add', params: { date: entry.date }}" tag="button"
                         class="h-full items-center flex bg-secondary absolute pin-t pin-r mr-2 text-white font-bold font-heading uppercase"
                         v-if="entry && entry.id && entry.is_editable">
                <i class="fas fa-pencil mr-2"></i>
                Edit
            </router-link>
        </top-nav-bar>

        <div v-if="loading" class="h-full flex items-center justify-center pt-24">
            <div class="spinner-blue"></div>
        </div>

        <div v-if="!loading && entry" class="py-2 clearfix">
            <h3 class="text-center">
                {{ formatDate(entry.date, 'D MMM YYYY') }}
            </h3>

            <list>
                <list-item>
                    <template slot="icon">
                        <i class="far" :class="findObjectStatusIconStyles(entry.status)"></i>
                    </template>
                    <template slot="title">Current Status</template>
                    <template slot="sub-title">
                        <span class="font-heading uppercase text-xs font-bold"
                              :class="findObjectStatusStyles(entry.status)">
                            {{ entry.status.status }}
                        </span>
                    </template>
                </list-item>
                <list-item>
                    <template slot="icon">
                        <i class="text-blue far fa-question-circle"></i>
                    </template>
                    <template slot="title">From - To</template>
                    <template slot="sub-title">
                        <div v-for="(interval, index) in entry.intervals" :key="`interval-${index}`">
                            {{ interval.start }} - {{ interval.end }}
                        </div>
                    </template>
                </list-item>
                <list-item>
                    <template slot="icon">
                        <i class="text-blue far fa-question-circle"></i>
                    </template>
                    <template slot="title">Duration</template>
                    <template slot="sub-title">{{ getTotalDuration(entry.intervals) }}</template>
                </list-item>
                <list-item>
                    <template slot="icon">
                        <i class="text-blue far fa-question-circle"></i>
                    </template>
                    <template slot="title">Rest Day</template>
                    <template slot="sub-title">{{ entry.rest_day ? 'Yes' : 'No' }}</template>
                </list-item>
            </list>

            <div class="bg-white mb-8">
                <h3 class="py-4 border-b border-grey-light flex items-center justify-center">
                    <span class="mx-2">Comments</span>
                    <button v-if="entry && entry.status.value !== 'approved'"
                            class="btn btn--sm btn--outline__green rounded py-2 mx-2"
                            :class="{'disabled': disabled}"
                            :disabled="disabled"
                            @click="addComment">
                        <i class="fal fa-spin fa-circle-notch" v-if="disabled"></i>
                        <i class="fal fa-plus"></i>
                    </button>
                </h3>
                <div v-for="comment in sortedComments" :key="comment.type + comment.id">
                    <div class="relative border-b border-grey-light px-4 pb-4 mt-2">
                        <div class="flex items-center">
                            <div class="flex-1">
                                <h5>
                                    {{comment.creator.name}}
                                    <span class="font-normal rounded-full bg-grey-light py-1 px-2 mx-2">
                                        {{comment.creator.type}}
                                    </span>
                                </h5>
                            </div>
                            <div class="text-xs mr-4" v-text="ago(comment.created_at)"></div>
                        </div>
                        <div v-if="!(comment.type === 'status' && comment.commentable.value === 'approved')"
                             class="mt-4 text-sm" v-html="comment.body"></div>
                        <div v-if="comment.type === 'status'" class="mt-4 flex items-center justify-center">
                            <span class="text-xs py-1 px-2 text-white uppercase rounded-full"
                                  :class="{
                                    'bg-red-light': comment.commentable.value === 'rejected',
                                    'bg-green-light': comment.commentable.value === 'approved'
                                  }">
                                {{ comment.creator.name }} {{ comment.commentable.status }} This Entry
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="entry.status && entry.status.value === 'pending'"
                 class="block md:inline-flex md:float-right px-4">
                <button class="btn btn--colored__red w-full"
                        :class="{'disabled': disabled}"
                        :disabled="disabled"
                        @click="deleteEntry">
                    <i class="fal fa-spin fa-circle-notch" v-if="disabled"></i>
                    <i class="fal fa-trash"></i>
                    Delete Entry
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import InteractsWithDates from '@/mixins/InteractsWithDates';
import NeedsTimesheetsDuration from '@/mixins/NeedsTimesheetsDuration';
import ShowObjectStatus from '@/mixins/ShowObjectStatus';
import CanAccessView from "@/mixins/CanAccessView";

export default {

    mixins: [InteractsWithDates, ShowObjectStatus, CanAccessView, NeedsTimesheetsDuration],

    data() {
        return {
            loading: false,
            disabled: false,
            entry: null,
        }
    },

    computed: {
        sortedComments() {
            return _.sortBy(this.entry.comments, function (comment) {
                return comment.created_at;
            }).reverse();
        },
    },

    mounted() {
        this.loading = true;
        this.$api('timesheets').get(this.$route.params.entry)
            .then(({data}) => {
                this.entry = data.data;
            })
            .finally(() => this.loading = false);
    },

    methods: {
        addComment() {
            if (this.entry && this.entry.status.value === 'approved') {
                return false;
            }

            this.$swal({
                title: 'Add a Comment',
                input: 'textarea',
                confirmButtonText: 'Save',
                showCancelButton: true,
            }).then((body) => {
                this.disabled = true;
                body && this.$api('timesheets').post(`${this.$route.params.entry}/comments`, {body})
                    .then(({data}) => {
                        this.entry.comments.push(data.data);
                        this.$noty.success('The Comment was successfully saved');
                    })
                    .catch((error) => {
                        if (error.response.data.errors.errorMessage) {
                            this.$noty.error(error.response.data.errors.errorMessage[0]);
                        }
                    })
                    .finally(() => this.disabled = false);
            }).catch(() => {
                //
            });
        },

        deleteEntry() {
            this.$swal({
                title: 'Are you sure?',
                html: 'This will delete the entry. The entry will not be reviewed.',
                confirmButtonText: 'Yes, delete entry.',
                showCancelButton: true
            }).then(() => {
                this.disabled = true;
                this.$api('timesheets').delete(`${this.$route.params.entry}`)
                    .then(() => {
                        this.$router.push('/timesheets');
                        this.$noty.success('Entry successfully deleted');
                    })
                    .finally(() => this.disabled = false);
            }).catch(() => {
                //
            });
        }
    }
}
</script>
